"use client"

import React, { useRef, useState, useEffect } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  CircularProgress,
  Box,
  Tooltip,
} from "@mui/material"

interface Props {
  headers: React.ReactNode[]
  data: any[]
  totalAmount?: number
  totalPaidAmount?: number
  totalUnpaidAmount?: number
  footerTotals?: {
    subTotal?: number
    grandTotal?: number
    payment?: number
    due?: number
    advance?: number
    discount?: number
    totalCount?: number
  } | null
  loading?: boolean
  page?: number
  rowsPerPage?: number
  height?: string | number
  handleChangePage?: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void
  handleChangeRowsPerPage?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  totalRows?: number
  actionButtons?: {
    icon?: React.ReactNode | ((rowIndex: number) => React.ReactNode)
    onClick?: (rowIndex: number, rowData: any) => void
    disabled?: (rowIndex: number) => boolean
    tooltip?: string | ((rowIndex: number) => string)
  }[]
  rowStyleCondition?: (rowIndex: number) => {
    sx: React.CSSProperties
    className: string
  }
}

// Custom component to handle truncation and tooltip
const TableCellWithTooltip: React.FC<{
  children: React.ReactNode
  sx?: React.CSSProperties
}> = ({ children, sx }) => {
  const cellRef = useRef<HTMLTableCellElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  useEffect(() => {
    const checkTruncation = () => {
      if (cellRef.current) {
        const { scrollWidth, offsetWidth } = cellRef.current
        setIsTruncated(scrollWidth > offsetWidth)
      }
    }

    checkTruncation()
    // Re-check on window resize
    window.addEventListener("resize", checkTruncation)
    return () => window.removeEventListener("resize", checkTruncation)
  }, [children])

  const cellContent = (
    <TableCell
      ref={cellRef}
      sx={{
        fontSize: {
          md: "0.75rem",
          xs: "0.65rem",
          xl: "0.75rem",
          sm: "0.65rem",
          lg: "0.75rem",
        },
        color: "#374151",
        py: 0.5,
        px: 1.5,
        textTransform: "none",
        lineHeight: 1.1,
        fontWeight: 400,
        borderBottom: "none",
        maxWidth: 120,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        cursor: "default",
        ...sx,
      }}
    >
      {children}
    </TableCell>
  )

  return isTruncated ? (
    <Tooltip title={children} arrow>
      {cellContent}
    </Tooltip>
  ) : (
    cellContent
  )
}

const DynamicTable: React.FC<Props> = ({
  headers,
  data,
  loading = false,
  page = 0,
  totalAmount = 0,
  totalPaidAmount = 0,
  totalUnpaidAmount = 0,
  footerTotals = null,
  rowsPerPage = 100,
  handleChangePage,
  handleChangeRowsPerPage,
  totalRows = 0,
  actionButtons,
  rowStyleCondition,
  height = "75vh",
}) => {
  return (
    <Box
      sx={{
        width: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          maxHeight: {
            md: totalRows > 0 ? `calc(${height} - 64px)` : `${height}`,
            sm: totalRows > 0 ? "calc(100vh - 64px)" : "100vh",
            xs: totalRows > 0 ? "calc(100vh - 64px)" : "100vh",
          },
          width: "100%",
          overflow: "auto",
          border: "1px solid #e5e7eb",
          borderRadius: totalRows > 0 ? "12px 0 0 0" : "12px 0 0 12px",
          backgroundColor: "#ffffff",
          "&::-webkit-scrollbar": {
            width: "6px",
            height: "6px",
            position: "absolute",
            zIndex: 6,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#077DD3",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f8fafc",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-corner": {
            backgroundColor: "#f8fafc",
          },
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {headers?.map((header, index) => (
                <TableCell
                  key={index}
                  sx={{
                    fontSize: {
                      md: "0.75rem",
                      xs: "0.65rem",
                      xl: "0.75rem",
                      sm: "0.65rem",
                      lg: "0.75rem",
                    },
                    fontWeight: 600,
                    backgroundColor: "#077DD3",
                    color: "#fff",
                    borderBottom: "2px solid #e5e7eb",
                    py: 1.5,
                    px: 1.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    lineHeight: 1.1,
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={headers.length + (actionButtons ? 1 : 0)}
                  sx={{
                    textAlign: "center",
                    py: 6,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <CircularProgress size={32} sx={{ color: "#6366f1" }} />
                </TableCell>
              </TableRow>
            ) : data?.length === 0 || data === null ? (
              <TableRow>
                <TableCell
                  colSpan={headers.length + (actionButtons ? 1 : 0)}
                  sx={{
                    textAlign: "center",
                    py: 6,
                    color: "#6b7280",
                    fontSize: "0.75rem",
                  }}
                >
                  No data available
                </TableCell>
              </TableRow>
            ) : (
              data?.map((row, rowIndex) => {
                const { sx, className } = rowStyleCondition ? rowStyleCondition(rowIndex) : { sx: {}, className: "" }

                // Check if this is the last row and first cell contains "TOTAL"
                const isLastRow = rowIndex === data.length - 1
                const firstCellValue = Object.values(row ?? {})?.[0]
                const isTotalRow = isLastRow && firstCellValue === "TOTAL"

                return (
                  <TableRow
                    key={rowIndex}
                    className={className}
                    sx={{
                      cursor: "default",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        backgroundColor: "#f8fafc",
                        transform: "translateY(-1px)",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                      },
                      "&:nth-of-type(even)": {
                        backgroundColor: "#fafafa",
                        "&:hover": {
                          backgroundColor: "#f1f5f9",
                        },
                      },
                      borderBottom: "1px solid #f1f5f9",
                      ...(isTotalRow && {
                        backgroundColor: "#e3f2fd",
                        fontWeight: 700,
                        "&:hover": {
                          backgroundColor: "#bbdefb",
                        },
                      }),
                      ...sx,
                    }}
                  >
                    {Object.values(row ?? {})?.map((cell: any, cellIndex) => (
                      <TableCellWithTooltip key={cellIndex} sx={isTotalRow ? { fontWeight: 700 } : {}}>
                        {cell}
                      </TableCellWithTooltip>
                    ))}
                    {actionButtons && actionButtons?.length > 0 && (
                      <TableCell
                        sx={{
                          py: 0.5,
                          px: 1.5,
                          borderBottom: "none",
                          textAlign: "center",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.25,
                            justifyContent: "center",
                          }}
                        >
                          {actionButtons?.map((button, buttonIndex) => {
                            const isDisabled = button.disabled?.(rowIndex) ?? false
                            const tooltipTitle =
                              typeof button.tooltip === "function"
                                ? button.tooltip(rowIndex)
                                : button.tooltip ?? ""

                            const iconButton = (
                              <span
                                style={
                                  isDisabled ? { cursor: "not-allowed" } : undefined
                                }
                              >
                                <IconButton
                                  onClick={() =>
                                    button.onClick?.(rowIndex, row)
                                  }
                                  disabled={isDisabled}
                                  size="small"
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    color: "#6b7280",
                                    transition:
                                      "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                    "&:hover": {
                                      backgroundColor: isDisabled
                                        ? "transparent"
                                        : "#e5e7eb",
                                      color: isDisabled ? "#B0B0B0" : "#374151",
                                      transform: isDisabled
                                        ? "none"
                                        : "scale(1.1)",
                                    },
                                    "&:active": {
                                      transform: isDisabled
                                        ? "none"
                                        : "scale(0.95)",
                                    },
                                    "&:disabled": {
                                      color: "#B0B0B0",
                                      cursor: "not-allowed",
                                    },
                                    "&:disabled .MuiSvgIcon-root": {
                                      color: "#B0B0B0 !important",
                                    },
                                  }}
                                >
                                  {typeof button.icon === "function"
                                    ? button.icon(rowIndex)
                                    : button.icon}
                                </IconButton>
                              </span>
                            )

                            return (
                              <React.Fragment key={buttonIndex}>
                                {button.icon &&
                                  (tooltipTitle ? (
                                    <Tooltip title={tooltipTitle} arrow>
                                      {iconButton}
                                    </Tooltip>
                                  ) : (
                                    iconButton
                                  ))}
                              </React.Fragment>
                            )
                          })}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Total Amount Display - Visible when any total > 0 */}
      {((totalAmount || 0) > 0 || (totalPaidAmount || 0) > 0 || (totalUnpaidAmount || 0) > 0) && totalRows !== 0 && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 10,
            width: "100%",
            border: "1px solid #e5e7eb",
            borderTop: "none",
            borderRadius: "0 0 0 12px",
            backgroundColor: "#fafafa",
            padding: "16px",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 4,
            flexWrap: "wrap"
          }}
        >
          {totalAmount && (
            <span
              style={{
                fontWeight: 600,
                color: "#374151",
                fontSize: "0.875rem",
              }}
            >
              <span style={{ fontWeight: 700 }}>Total Amount: Rs. </span>
              {totalAmount?.toLocaleString() || ""}/-
            </span>
          )}
          {(totalPaidAmount > 0) && (
            <span
              style={{
                fontWeight: 600,
                color: "#15803d",
                fontSize: "0.875rem",
              }}
            >
              <span style={{ fontWeight: 700 }}>Total Paid : Rs. </span>
              {totalPaidAmount?.toLocaleString() || "0.00"}/-
            </span>
          )}
          {(totalUnpaidAmount > 0) && (
            <span
              style={{
                fontWeight: 600,
                color: "#ff0000",
                fontSize: "0.875rem",
              }}
            >
              <span style={{ fontWeight: 700 }}>Total Unpaid : Rs. </span>
              {totalUnpaidAmount?.toLocaleString() || "0.00"}/-
            </span>
          )}
        </Box>
      )}

      {totalRows > 0 && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 10,
            width: "100%",
            border: "1px solid #e5e7eb",
            borderTop: "none",
            borderRadius: "0 0 0 12px",
            backgroundColor: "#fafafa",
          }}
        >
          {/* Footer Totals Section */}
          {footerTotals && (
            <Box
              sx={{
                display: "flex",
                width: "100%",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 0.75, sm: 1.25 },
                alignItems: { xs: "flex-start", sm: "center" },
                px: { xs: 1.5, sm: 2 },
                py: { xs: 1, sm: 1 },
                flexWrap: "wrap",
                fontSize: "0.75rem",
                color: "#374151",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <Box>
                <span className="font-bold">Sub Total:</span> {Number(footerTotals.subTotal || 0).toLocaleString()}
              </Box>
              <Box>
                <span className="font-bold">Total:</span> {Number(footerTotals.grandTotal || 0).toLocaleString()}
              </Box>
              <Box>
                <span className="font-bold">Paid Amount:</span> {Number(footerTotals.payment || 0).toLocaleString()}
              </Box>
              <Box sx={{ color: (footerTotals.due || 0) > 0 ? "red" : "inherit", fontWeight: 600 }}>
                <span className="font-bold">Due Amount:</span> {Number(footerTotals.due || 0).toLocaleString()}
              </Box>
              <Box>
                <span className="font-bold">Advance:</span> {Number(footerTotals.advance || 0).toLocaleString()}
              </Box>
            </Box>
          )}
          {/* Pagination Controls */}
          <TablePagination
            sx={{
              fontSize: {
                md: "0.75rem",
                xs: "0.65rem",
                xl: "0.75rem",
                sm: "0.65rem",
                lg: "0.75rem",
              },
              color: "#374151",
              "& .MuiTablePagination-toolbar": {
                minHeight: { xs: "auto", sm: "64px" },
                px: { xs: 1.5, sm: 2 },
                py: { xs: 1.5, sm: 0 },
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "flex-start", sm: "space-between" },
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 1, sm: 0 },
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "0.75rem",
                color: "#6b7280",
                fontWeight: 500,
                margin: 0,
                alignSelf: { xs: "flex-start", sm: "auto" },
              },
              "& .MuiTablePagination-select": {
                fontSize: "0.75rem",
                color: "#374151",
                fontWeight: 500,
              },
              "& .MuiIconButton-root": {
                color: "#6b7280",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  backgroundColor: "#e5e7eb",
                  color: "#374151",
                  transform: "scale(1.1)",
                },
                "&:disabled": {
                  color: "#d1d5db",
                },
              },
            }}
            rowsPerPageOptions={[100, 150, 200]}
            component="div"
            count={totalRows}
            rowsPerPage={rowsPerPage || 100}
            page={page}
            onPageChange={handleChangePage || (() => {})}
            onRowsPerPageChange={handleChangeRowsPerPage || (() => {})}
            labelDisplayedRows={({ from, to, count }) => (
              <span>
                {from}–{to} of {count !== -1 ? count : `more than ${to}`}
              </span>
            )}
          />
        </Box>
      )}
    </Box>
  )
}

export default DynamicTable
